// The new hire's own page (2026-10-24): /new-hire/start?t=<token>
//
// NO LOGIN and no app chrome. The office or a foreman sent this link by text and/or
// email; the link token is the only key, and the server (api/new-hires public-*
// routes) decides everything: an expired, voided or already-used link gets the same
// "no longer valid" answer. The form is the SAME IntakeForm the staff use
// (NewHireView), in English or Spanish, with the job / union / trade / start date
// read-only because the sender set them.
//
// Behind the same build flag as the New Hires tab (REACT_APP_NEW_HIRE_ENABLED).
import React, { useEffect, useMemo, useState } from 'react'
import { IntakeForm } from '../components/NewHireView'
import { newHirePublicApi } from '../lib/db'

const ON = process.env.REACT_APP_NEW_HIRE_ENABLED === 'true'

const DEAD = {
  en: 'This link is no longer valid - ask your foreman or the office for a new one.',
  es: 'Este enlace ya no es válido - pida uno nuevo a su capataz o a la oficina.',
}

const shell = { minHeight: '100vh', background: 'var(--surface2, #f5f5f3)', padding: '16px 12px 40px' }
const col = { maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }

function Brand() {
  return <div style={{ fontWeight: 700, fontSize: 16, color: '#185FA5' }}>Cornerstone Contracting</div>
}

function LangToggle({ lang, setLang }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
      {[['en', 'English'], ['es', 'Español']].map(([k, l]) => (
        <button key={k} type="button" className={'btn btn-sm' + (lang === k ? ' btn-primary' : '')} onClick={() => setLang(k)}>{l}</button>
      ))}
    </div>
  )
}

export default function NewHireSelfPage() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('t') || '', [])
  const [lang, setLang] = useState('en')
  const [invite, setInvite] = useState(null)
  const [today, setToday] = useState('')
  const [dead, setDead] = useState(false)
  const [loadErr, setLoadErr] = useState('')
  const [done, setDone] = useState(null)

  useEffect(() => {
    if (!ON) return
    if (!token) { setDead(true); return }
    newHirePublicApi(token, '').get('get').then(r => {
      if (r.status === 410 || r.error === 'link_invalid') { setDead(true); return }
      if (r.error || !r.invite) { setLoadErr(r.detail || r.error || 'error'); return }
      setInvite(r.invite); setToday(r.today || ''); setLang(r.invite.language || 'en')
    })
  }, [token])

  const api = useMemo(() => newHirePublicApi(token, lang), [token, lang])
  const L = (en, es) => (lang === 'es' ? es : en)

  if (!ON) {
    return <div style={shell}><div style={col}><Brand /><div className="card" style={{ padding: 16 }}>This page is not available.</div></div></div>
  }

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Brand />
      <LangToggle lang={lang} setLang={setLang} />
    </div>
  )

  if (dead) {
    return (
      <div style={shell}><div style={col}>
        {header}
        <div className="card" style={{ padding: 16, fontSize: 14 }}>
          <div>{DEAD[lang]}</div>
          <div style={{ marginTop: 8, color: 'var(--text3)' }}>{DEAD[lang === 'es' ? 'en' : 'es']}</div>
        </div>
      </div></div>
    )
  }
  if (loadErr) {
    return <div style={shell}><div style={col}>{header}<div className="card" style={{ padding: 16, color: '#A32D2D' }}>{L('Something went wrong loading the form - try the link again.', 'Algo salió mal al abrir el formulario - abra el enlace otra vez.')}</div></div></div>
  }
  if (!invite) {
    return <div style={shell}><div style={col}>{header}<div style={{ color: 'var(--text3)' }}>{L('Loading...', 'Cargando...')}</div></div></div>
  }

  if (done) {
    return (
      <div style={shell}><div style={col}>
        {header}
        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 600, fontSize: 17, color: '#3B6D11', marginBottom: 8 }}>{L('Thank you', 'Gracias')}</div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>
            {done.email
              ? L(`Your form was sent to Cornerstone. Once the office reviews it, you'll get a text message and an email at ${done.email} from DocuSign to fill out and sign your W-4 and the rest of your new hire paperwork. Look for a message from DocuSign.`,
                `Su formulario fue enviado a Cornerstone. Cuando la oficina lo revise, usted recibirá un mensaje de texto y un correo a ${done.email} de DocuSign para llenar y firmar su W-4 y los demás documentos de nuevo empleado. Busque un mensaje de DocuSign.`)
              : L("Your form was sent to Cornerstone. Once the office reviews it, you'll get a text message from DocuSign to fill out and sign your W-4 and the rest of your new hire paperwork. Look for a message from DocuSign.",
                'Su formulario fue enviado a Cornerstone. Cuando la oficina lo revise, usted recibirá un mensaje de texto de DocuSign para llenar y firmar su W-4 y los demás documentos de nuevo empleado. Busque un mensaje de DocuSign.')}
          </div>
        </div>
      </div></div>
    )
  }

  // The staff form expects an `options` object; this one carries only what the page needs.
  const options = { jobs: [], union_locals: [], union_trades: [], cert_types: invite.cert_types || [], address_lookup: !!invite.address_lookup, today }
  return (
    <div style={shell}><div style={col}>
      {header}
      <IntakeForm options={options} initial={null} isMobile={window.innerWidth < 700}
        self={{ api, lang, invite }}
        onDone={(r) => { setDone({ email: r.email || null }); window.scrollTo && window.scrollTo(0, 0) }} />
    </div></div>
  )
}
